/**
 * Formatação do Painel de Custos.
 *
 * Regra central: `cost` chega como string de precisão arbitrária (`numeric` do Postgres
 * com a escala enxugada). Nada aqui converte dinheiro para `number` antes de formatar —
 * o arredondamento é feito sobre os dígitos, com carry manual. A única exceção está em
 * `costToChartValue`, e está documentada lá.
 */

/** String numérica decimal aceita pelo formatador (o backend nunca envia notação científica). */
const DECIMAL_PATTERN = /^-?\d+(\.\d+)?$/;

/** Valor ausente ou não numérico. */
const EMPTY_LABEL = '—';

/** Casas decimais padrão e o "zoom" usado quando 2 casas zerariam um valor não nulo. */
const DEFAULT_DECIMALS = 2;
const SMALL_VALUE_DECIMALS = 4;

/**
 * Símbolo por moeda. A moeda vem da resposta (`currency`), nunca cravada no template:
 * hoje é sempre USD, mas o backend passa a variar o campo quando entrar um provider
 * que fature em outra moeda. Moeda desconhecida usa o próprio código como prefixo.
 */
const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: 'US$',
  BRL: 'R$',
  EUR: '€'
};

export function currencySymbol(currency: string): string {
  const code = (currency || '').trim().toUpperCase();
  return CURRENCY_SYMBOLS[code] || code || '';
}

/** True quando a string decimal representa zero (qualquer escala). */
function isZeroString(value: string): boolean {
  return /^-?0*(\.0*)?$/.test(value);
}

/**
 * Arredonda uma string decimal para `places` casas, meio-para-cima, operando sobre os
 * dígitos. Não passa por `Number`, então uma string longa como `"2.1495945"` não perde
 * precisão no caminho e valores maiores que 2^53 não estouram.
 */
function roundDecimalString(value: string, places: number): { intPart: string; fracPart: string; negative: boolean } {
  const negative = value.startsWith('-');
  const [intRaw, fracRaw = ''] = (negative ? value.slice(1) : value).split('.');

  const digits = (intRaw + fracRaw.slice(0, places).padEnd(places, '0')).split('');
  const nextDigit = fracRaw.charAt(places);

  if (nextDigit >= '5') {
    let index = digits.length - 1;
    while (index >= 0) {
      if (digits[index] === '9') {
        digits[index] = '0';
        index--;
      } else {
        digits[index] = `${Number(digits[index]) + 1}`;
        break;
      }
    }
    if (index < 0) { digits.unshift('1'); }   // carry estourou (ex.: 9,99 → 10,00)
  }

  const intLength = digits.length - places;
  return {
    intPart: digits.slice(0, intLength).join('') || '0',
    fracPart: digits.slice(intLength).join(''),
    negative
  };
}

/** Agrupa milhar com ponto (pt-BR), sem passar por `Number`. */
function groupThousands(intPart: string): string {
  return intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

/** Junta as partes no formato pt-BR, descartando o sinal de um zero negativo. */
function toPtBrNumber(parts: { intPart: string; fracPart: string; negative: boolean }): string {
  const body = parts.fracPart
    ? `${groupThousands(parts.intPart)},${parts.fracPart}`
    : groupThousands(parts.intPart);
  const isZero = /^0*$/.test(parts.intPart) && /^0*$/.test(parts.fracPart);
  return parts.negative && !isZero ? `-${body}` : body;
}

/**
 * Formata apenas o valor numérico do custo: `"12.828714"` → `12,83`.
 *
 * Usa 2 casas, e sobe para 4 quando 2 casas fariam um valor não nulo virar zero —
 * `0,0021` some se arredondar para 2. Abaixo de 0,0001 mostra `< 0,0001`.
 */
export function formatCostAmount(raw: string | undefined | null): string {
  const value = (raw ?? '').trim();
  if (!DECIMAL_PATTERN.test(value)) { return EMPTY_LABEL; }

  const rounded = roundDecimalString(value, DEFAULT_DECIMALS);
  const roundedIsZero = /^0*$/.test(rounded.intPart) && /^0*$/.test(rounded.fracPart);

  if (!roundedIsZero || isZeroString(value)) {
    return toPtBrNumber(rounded);
  }

  const precise = roundDecimalString(value, SMALL_VALUE_DECIMALS);
  if (/^0*$/.test(precise.intPart) && /^0*$/.test(precise.fracPart)) {
    return '< 0,0001';
  }
  return toPtBrNumber(precise);
}

/** Formata um custo para exibição: `"12.828714"` → `US$ 12,83`. */
export function formatCost(raw: string | undefined | null, currency: string): string {
  const amount = formatCostAmount(raw);
  if (amount === EMPTY_LABEL) { return amount; }

  const symbol = currencySymbol(currency);
  const prefix = symbol ? `${symbol} ` : '';
  return amount.startsWith('< ') ? `< ${prefix}${amount.slice(2)}` : `${prefix}${amount}`;
}

/**
 * Soma custos mantendo a precisão da string. Usado quando a tela mescla duas fatias do
 * breakdown numa linha só (ex.: `comparison` e `comparison-upload`, que exibem o mesmo
 * rótulo) — somar via `Number` reintroduziria o erro de ponto flutuante justamente no
 * valor que a tabela mostra e o usuário copia.
 *
 * `BigInt` sobre os dígitos alinhados na maior escala: exato por construção, sem o carry
 * manual de `roundDecimalString`. Valores não numéricos entram como zero, como no resto
 * do módulo. A escala do resultado é a maior das entradas (a formatação arredonda depois).
 */
export function sumCostStrings(values: Array<string | undefined | null>): string {
  const parsed = values
    .map(raw => (raw ?? '').trim())
    .filter(value => DECIMAL_PATTERN.test(value))
    .map(value => {
      const [intPart, fracPart = ''] = value.split('.');
      return { intPart, fracPart };
    });

  if (!parsed.length) { return '0'; }

  const scale = Math.max(...parsed.map(part => part.fracPart.length));
  const total = parsed.reduce(
    (sum, part) => sum + BigInt(part.intPart + part.fracPart.padEnd(scale, '0')),
    BigInt(0)
  );

  if (!scale) { return `${total}`; }

  const negative = total < BigInt(0);
  const digits = (negative ? -total : total).toString().padStart(scale + 1, '0');
  const body = `${digits.slice(0, digits.length - scale)}.${digits.slice(digits.length - scale)}`;
  return negative ? `-${body}` : body;
}

/**
 * Custo para a célula do CSV: troca o ponto decimal por vírgula na string, mantendo a
 * precisão integral. Nunca `Number(cost).toFixed()` — é a mesma regra do `formatCost`,
 * e aqui o valor ainda vai ser somado numa planilha.
 */
export function costToCsvDecimal(raw: string | undefined | null): string {
  const value = (raw ?? '').trim();
  if (!DECIMAL_PATTERN.test(value)) { return '0'; }
  return value.replace('.', ',');
}

/**
 * Único ponto do módulo que converte custo para `number`, e só porque o `po-chart`
 * precisa de coordenadas numéricas. O valor alimenta a geometria do gráfico — nunca um
 * total exibido, somado ou copiado para planilha, que saem sempre de `formatCost`
 * sobre a string original.
 */
export function costToChartValue(raw: string | undefined | null): number {
  const value = Number((raw ?? '').trim());
  return Number.isFinite(value) ? value : 0;
}

/**
 * Valor exibido pelo tooltip nativo do `po-chart`. Como o PO-UI formata esse número
 * via `toString()`, limitamos a escala aqui para o tooltip não estourar no canto da tela.
 */
export function roundChartCostValue(value: number): number {
  if (!Number.isFinite(value)) { return 0; }
  const decimals = value !== 0 && Math.abs(value) < 0.01 ? SMALL_VALUE_DECIMALS : DEFAULT_DECIMALS;
  return Number(value.toFixed(decimals));
}

/**
 * Comparador de custos para ordenar (maior primeiro). Converter para número aqui é
 * seguro pelo mesmo motivo de `costToChartValue`: o resultado é uma ORDEM, não um valor
 * exibido — cada linha continua sendo formatada a partir da string original.
 */
export function compareCostsDesc(a: string | undefined | null, b: string | undefined | null): number {
  return costToChartValue(b) - costToChartValue(a);
}

/** Tokens com separador de milhar. Usado em tabela, de onde se copia valor. */
export function formatTokens(value: number | undefined | null): string {
  if (value == null || !Number.isFinite(value)) { return EMPTY_LABEL; }
  return value.toLocaleString('pt-BR');
}

/** Tokens abreviados (`4,1 M`). Só em KPI — nunca em tabela. */
export function abbreviateTokens(value: number | undefined | null): string {
  if (value == null || !Number.isFinite(value)) { return EMPTY_LABEL; }
  const abs = Math.abs(value);
  if (abs >= 1_000_000) { return `${(value / 1_000_000).toFixed(1).replace('.', ',')} M`; }
  if (abs >= 1_000) { return `${(value / 1_000).toFixed(1).replace('.', ',')} k`; }
  return formatTokens(value);
}

/**
 * `YYYY-MM-DD` → `DD/MM/YYYY` por split da string. Sem `new Date`, que interpretaria a
 * data como UTC e recuaria um dia em UTC-3.
 */
export function formatApiDate(raw: string | undefined | null): string {
  const value = (raw ?? '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) { return value || EMPTY_LABEL; }
  const [year, month, day] = value.split('-');
  return `${day}/${month}/${year}`;
}

/** `DD/MM` — rótulo curto do eixo X da série diária. */
export function formatApiDateShort(raw: string | undefined | null): string {
  const full = formatApiDate(raw);
  return full.length === 10 ? full.slice(0, 5) : full;
}

/**
 * Dias entre duas datas `YYYY-MM-DD`, incluindo as duas pontas (09/07 a 09/07 = 1 dia).
 * `new Date(ano, mês, dia)` a partir dos componentes já separados é seguro — quem
 * quebraria seria parsear a string inteira (`new Date(iso)`), que o JS interpreta como
 * UTC e recua um dia em UTC-3 (mesma regra do agrupamento semanal do gráfico).
 */
export function daysBetweenInclusive(from: string, to: string): number {
  const [fy, fm, fd] = from.split('-').map(Number);
  const [ty, tm, td] = to.split('-').map(Number);
  const start = new Date(fy, fm - 1, fd);
  const end = new Date(ty, tm - 1, td);
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.round((end.getTime() - start.getTime()) / msPerDay) + 1;
}

/** Variação de um custo contra outro, para o rótulo de comparação. */
export interface CostVariation {
  /** `+23%`, `-8%`, `0%`. */
  label: string;
  /** True quando subiu — a tela usa para escolher a cor do indicador. */
  up: boolean;
  /** True quando não houve mudança. */
  flat: boolean;
}

/**
 * Variação percentual entre dois custos, para exibição.
 *
 * Aqui a conversão para número é aceitável e deliberada: o resultado é um rótulo de
 * tendência arredondado a um inteiro, nunca um valor que alguém copie para planilha.
 * Os valores monetários ao lado continuam vindo de `formatCost` sobre a string.
 *
 * Devolve `null` quando não há base de comparação — sem período anterior, ou com
 * custo anterior zero, caso em que qualquer gasto seria uma variação infinita.
 */
export function costVariation(current: string | undefined, previous: string | undefined): CostVariation | null {
  const now = costToChartValue(current);
  const before = costToChartValue(previous);
  if (!before) { return null; }

  const pct = Math.round(((now - before) / before) * 100);
  const sign = pct > 0 ? '+' : '';
  return { label: `${sign}${pct}%`, up: pct > 0, flat: pct === 0 };
}

/**
 * Quanto um custo representa de outro, como rótulo (`28%`). Devolve `null` sem base de
 * comparação — um total zero não tem fração.
 *
 * Converter para número aqui segue a mesma regra de `costVariation`: o resultado é um
 * percentual arredondado a inteiro, nunca um valor que alguém copie para planilha. Os
 * valores monetários ao lado continuam saindo de `formatCost` sobre a string.
 */
export function costSharePercent(part: string | undefined | null, total: string | undefined | null): string | null {
  const totalValue = costToChartValue(total);
  if (!totalValue) { return null; }
  return `${Math.round((costToChartValue(part) / totalValue) * 100)}%`;
}

/** Percentual inteiro de `part` sobre `total` (contagens, não dinheiro). `—` sem base. */
export function formatPercent(part: number | undefined, total: number | undefined): string {
  if (!total || !Number.isFinite(total) || part == null || !Number.isFinite(part)) { return EMPTY_LABEL; }
  return `${Math.round((part / total) * 100)}%`;
}
